import { anchorPictures, coverRight } from '../../domain/covers';
import { isEditor } from '../../domain/permissions';
import { displayName } from '../../domain/names';
import type { CommandResult, FamilyPhoto, MemoryAnchor, Notification, UUID } from '../../domain/types';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { AddPhotoCommand, PhotoRepository } from '../types';
import { actorOf, copies, denied, guarded, invalid, notFound, okResult, tryActor } from './actor';
import { byId } from '../../lib/grouped';
import { getBlob, mediumKey, putBlob, thumbKey } from './blobStore';
import type { MockDb } from './MockDb';
import { smallCopy, UrlCache } from './pictureUrls';

/**
 * How many urls are remembered before the oldest are let go (see UrlCache).
 * A full-size picture is a few hundred kilobytes held in memory, so only the
 * last few dozen anybody opened are kept; a thumbnail is a few kilobytes, and a
 * constellation wants hundreds of them at once, so that ceiling is far higher.
 */
const FULL_URLS = 48;
const THUMB_URLS = 1500;
const MEDIUM_URLS = 240;
/*
 * Clips and recordings are kept apart from photographs, and many more of them.
 * They cost almost nothing to remember — anything over a few megabytes is a
 * handle on the browser's storage, not bytes in memory — and they are the one
 * kind of url that is still being *read from* long after it was handed out: a
 * ring with sixty clips on it is sixty players each fetching a first frame, and
 * letting the forty-ninth push the first one's url out from under it would
 * leave that circle blank.
 */
const MEDIA_URLS = 400;
/** The side of the small square, in pixels: a 76px circle on a 3x phone, with a little to spare. */
const THUMB_SIDE = 192;
/** The long side of the medium copy: the top of a 200px card on a 3x phone. */
const MEDIUM_SIDE = 640;

export class MockPhotoRepository implements PhotoRepository {
  private urls = new UrlCache(FULL_URLS);
  private thumbs = new UrlCache(THUMB_URLS);
  private mediums = new UrlCache(MEDIUM_URLS);
  private media = new UrlCache(MEDIA_URLS);

  constructor(private db: MockDb) {}

  /**
   * The day a picture is being added to: what date it takes, who is already
   * pinned to it, and how a notification should name and link to it. Returns
   * undefined when there is no such day — a picture is never orphaned onto an
   * anchor that does not exist.
   */
  private anchorOf(anchor: MemoryAnchor, accountId: UUID): { takenOn?: FamilyPhoto['takenOn']; personIds: UUID[]; what: string; label: string; labelHe: string; link: string; tells?: Notification['kind']; quiet?: boolean } | undefined {
    // The family's line is where a link about a picture lands now; the day itself opens as its constellation.
    const link = `/timeline?memory=${anchor.kind}:${anchor.id}`;
    if (anchor.kind === 'recipe') {
      /*
       * A recipe is not a day: it gives the picture no date, and nobody is
       * pinned to a plate of fish. Anybody in the family may add to one that is
       * out. A draft is its writer's alone, pictures included — they can dress it
       * before anyone sees it, and nobody is told (`quiet`) about a recipe they
       * cannot open yet; the family hears about it once, when it is published.
       */
      const r = this.db.t.recipes.find((x) => x.id === anchor.id && x.status !== 'archived');
      if (!r || (r.status === 'draft' && r.creatorAccountId !== accountId)) return undefined;
      return { personIds: [], what: 'a recipe', label: r.doc.title || 'a recipe', labelHe: r.doc.title || 'מתכון', link: `/recipes/${r.id}`, tells: 'recipe', quiet: r.status === 'draft' };
    }
    if (anchor.kind === 'moment') {
      // Only a day of its own can be a parent; a picture inside one cannot.
      const m = this.db.t.family_photos.find((p) => p.id === anchor.id && !p.addedTo && !p.removed);
      if (!m) return undefined;
      const named = m.title || (m.milestone ? 'a family milestone' : 'an event');
      return { takenOn: m.takenOn, personIds: [...m.personIds], what: 'a family milestone', label: named, labelHe: m.title || 'רגע משפחתי', link };
    }
    if (anchor.kind === 'story') {
      const st = this.db.t.stories.find((x) => x.id === anchor.id && x.status === 'published');
      if (!st) return undefined;
      // Everyone the story is about, took part in or mentions, plus whoever told it.
      const people = new Set<UUID>([st.primaryStorytellerPersonId, ...this.db.t.story_people.filter((sp) => sp.storyId === st.id).map((sp) => sp.personId)]);
      const named = st.title || 'a story';
      return { takenOn: st.date, personIds: [...people], what: 'a story', label: named, labelHe: st.title || 'סיפור', link: `/stories/${st.id}` };
    }
    const person = this.db.t.persons.find((x) => x.id === anchor.id && !x.archived && !x.mergedIntoId);
    if (!person) return undefined;
    const names = this.db.t.person_names.filter((n) => n.personId === person.id);
    const en = displayName({ ...person, names }, 'en');
    const he = displayName({ ...person, names }, 'he');
    return { takenOn: person.birth, personIds: [person.id], what: 'a birth', label: `${en}'s early days`, labelHe: `הימים הראשונים של ${he}`, link };
  }

  async list(): Promise<FamilyPhoto[]> {
    await this.db.read();
    // Anything an administrator took down stays in storage for the decision log
    // but is no longer part of what the family sees.
    /*
     * Nor is a picture of a recipe the viewer cannot open. A draft belongs to
     * its writer, so the pictures on it are nobody else's yet; a recipe that was
     * taken down takes its pictures out of sight with it (and an administrator,
     * who can still see the recipe, still sees them, which is what makes putting
     * it back whole). Without this a half-written recipe would give itself away
     * as a picture on its writer's circle.
     */
    const me = tryActor(this.db)?.session;
    const closed = new Set(
      this.db.t.recipes.filter((r) => (r.status === 'draft' && r.creatorAccountId !== me?.accountId) || (r.status === 'archived' && me?.role !== 'admin')).map((r) => r.id),
    );
    // Copies, not the rows (see `copies`): a caption changed in place must reach a memoised row as a new object.
    return copies(
      this.db.t.family_photos
        .filter((p) => !p.removed && !(p.addedTo?.kind === 'recipe' && closed.has(p.addedTo.id)))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    );
  }

  async add(cmd: AddPhotoCommand): Promise<CommandResult<FamilyPhoto>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const existing = this.db.idem(cmd.idempotencyKey);
      if (existing) {
        const prior = this.db.t.family_photos.find((p) => p.id === existing.entityId);
        if (prior) return okResult(prior, { noop: true });
      }
      if (!cmd.image && !(cmd.isEvent && cmd.title?.trim())) return invalid('photos.needPictureOrEvent');
      const id = uuid();
      let storageKey: string | undefined;
      if (cmd.image) {
        storageKey = `photo/${id}`;
        await putBlob(storageKey, cmd.image);
      }
      /*
       * Adding to a day the family remembers — a simcha or an event, a story, a
       * birth. The new picture or clip hangs off that day, never becoming an
       * occasion in its own right, and takes the day's date and the people
       * pinned to it, so the family finds it three ways: inside the day, in
       * Moments, and under the circle of whoever added it. A record that is
       * itself part of something cannot be the parent, so the chain never grows
       * past one level.
       */
      const anchored = cmd.addedTo ? this.anchorOf(cmd.addedTo, actor.session.accountId) : undefined;
      if (cmd.addedTo && !anchored) return notFound();
      const photo: FamilyPhoto = {
        id, familyId: actor.session.familyId, uploadedByAccountId: actor.session.accountId, storageKey,
        mimeType: cmd.image ? cmd.image.type || 'image/jpeg' : undefined, bytes: cmd.image?.size, width: cmd.width, height: cmd.height, durationMs: cmd.durationMs,
        title: cmd.title?.trim() || undefined, caption: cmd.caption?.trim() || undefined,
        isEvent: anchored ? false : cmd.isEvent || !!cmd.milestone, milestone: anchored ? undefined : cmd.milestone,
        addedTo: anchored ? cmd.addedTo : undefined,
        takenOn: anchored ? anchored.takenOn ?? cmd.takenOn : cmd.takenOn,
        personIds: anchored ? [...new Set([...(cmd.personIds ?? []), ...anchored.personIds])] : cmd.personIds ?? [],
        createdAt: nowISO(),
      };
      this.db.t.family_photos.push(photo);
      // A picture on a draft is as private as the draft: no line in the family's history, and nobody told.
      if (!anchored?.quiet) this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'family', entityId: actor.session.familyId, action: 'create', description: anchored ? `Added to ${anchored.what}` : photo.milestone ? 'Shared a family milestone' : photo.isEvent ? 'Shared an event photo' : 'Shared a photo' });
      this.db.rememberIdem(cmd.idempotencyKey, 'photo', id);
      const who = this.db.accountName(actor.session.accountId);
      const what = photo.title || photo.caption;
      const clip = !!photo.mimeType?.startsWith('video/');
      const voice = !!photo.mimeType?.startsWith('audio/');
      if (!anchored?.quiet) this.db.notifyFamily(actor.session.accountId, anchored
        ? { kind: anchored.tells ?? 'moment', actorAccountId: actor.session.accountId, title: `${who} added ${voice ? 'a recording' : clip ? 'a video' : 'a picture'} to ${anchored.label}`, titleHe: `${who} הוסיף/ה ${voice ? 'הקלטה' : clip ? 'סרטון' : 'תמונה'} ל${anchored.labelHe}`, link: anchored.link }
        : photo.milestone
        ? { kind: 'event', actorAccountId: actor.session.accountId, title: `Mazal tov! ${who} shared a family milestone${what ? `: ${what}` : ''}`, titleHe: `מזל טוב! ${who} שיתף/ה רגע משפחתי גדול${what ? `: ${what}` : ''}`, link: `/timeline?memory=moment:${photo.id}` }
        : photo.isEvent
        ? { kind: 'event', actorAccountId: actor.session.accountId, title: `${who} added an event${what ? `: ${what}` : ''}`, titleHe: `${who} הוסיף/ה אירוע${what ? `: ${what}` : ''}`, link: '/timeline' }
        : { kind: 'moment', actorAccountId: actor.session.accountId, title: `${who} shared a photo${what ? `: ${what}` : ''}`, titleHe: `${who} שיתף/ה תמונה${what ? `: ${what}` : ''}`, link: '/timeline' });
      await this.db.commit(['family_photos', 'audit_events', 'idempotency', 'notifications']);
      /*
       * The small square is made now, by the phone that already has the picture
       * in its hands, rather than by whoever first opens the wedding — who would
       * otherwise pay for two hundred of them at once. Not waited for: the
       * picture is saved either way, and `getThumbUrl` makes any that are missing.
       */
      if (storageKey && photo.mimeType?.startsWith('image/')) void smallCopy(thumbKey(id), storageKey, THUMB_SIDE);
      return okResult(photo);
    });
  }

  async update(id: UUID, patch: Partial<Pick<FamilyPhoto, 'caption' | 'isEvent' | 'milestone' | 'takenOn' | 'personIds' | 'title'>>): Promise<CommandResult<FamilyPhoto>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const photo = this.db.t.family_photos.find((p) => p.id === id);
      if (!photo) return notFound();
      if (photo.uploadedByAccountId !== actor.session.accountId && !isEditor(actor.session.role)) return denied();
      Object.assign(photo, patch);
      // A milestone is always an event; taking the event flag off takes the milestone off too.
      if (patch.isEvent === false) photo.milestone = undefined;
      if (photo.milestone) photo.isEvent = true;
      // Something added to a day the family remembers belongs to it, and is never an occasion of its own.
      if (photo.addedTo) {
        photo.isEvent = false;
        photo.milestone = undefined;
      }
      await this.db.commit(['family_photos']);
      return okResult(photo);
    });
  }

  async tagPerson(id: UUID, personId: UUID, on: boolean): Promise<CommandResult<FamilyPhoto>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const photo = this.db.t.family_photos.find((p) => p.id === id);
      if (!photo) return notFound();
      if (!this.db.t.persons.some((p) => p.id === personId)) return notFound();
      const has = photo.personIds.includes(personId);
      if (on === has) return okResult(photo, { noop: true });
      photo.personIds = on ? [...photo.personIds, personId] : photo.personIds.filter((x) => x !== personId);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'family', entityId: actor.session.familyId, action: 'update', description: on ? 'Pinned a person to a photo' : 'Removed a person from a photo' });
      await this.db.commit(['family_photos', 'audit_events']);
      return okResult(photo);
    });
  }

  async react(id: UUID, emoji: string | null): Promise<CommandResult<FamilyPhoto>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const photo = this.db.t.family_photos.find((p) => p.id === id);
      if (!photo) return notFound();
      const others = (photo.reactions ?? []).filter((r) => r.accountId !== actor.session.accountId);
      photo.reactions = emoji ? [...others, { accountId: actor.session.accountId, emoji, createdAt: nowISO() }] : others;
      await this.db.commit(['family_photos']);
      return okResult(photo);
    });
  }

  async setCover(anchor: MemoryAnchor, photoId: UUID | null): Promise<CommandResult<FamilyPhoto | null>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      // A recipe names its own main picture (RecipeRepository.setCover).
      if (anchor.kind === 'recipe') return invalid('error.unexpected');
      // The thing itself has to be there: a published story, a day of its own, a circle still in the tree.
      if (!this.anchorOf(anchor, actor.session.accountId)) return notFound();
      const things = { moments: this.db.t.family_photos, stories: this.db.t.stories, recipes: this.db.t.recipes };
      if (coverRight(actor, anchor, things) === 'none') return denied();
      // Only a still that is part of this thing, and one that is still there to be seen.
      const pictures = anchorPictures(this.db.t.family_photos, anchor);
      const chosen = photoId ? pictures.find((p) => p.id === photoId) : undefined;
      if (photoId && !chosen) return notFound();
      if (pictures.every((p) => !!p.isCover === (p === chosen))) return okResult(chosen ?? null, { noop: true });
      // One mark at most: the rest are cleared in the same breath, so two can never both claim it.
      for (const p of this.db.t.family_photos) {
        if (p.isCover && (p.id === anchor.id || (p.addedTo && p.addedTo.kind === anchor.kind && p.addedTo.id === anchor.id))) p.isCover = undefined;
      }
      if (chosen) chosen.isCover = true;
      await this.db.commit(['family_photos'], chosen ? [chosen.id] : undefined);
      return okResult(chosen ?? null);
    });
  }

  async getUrl(id: UUID): Promise<string | undefined> {
    await this.db.ready;
    const photo = byId(this.db.t.family_photos).get(id);
    if (!photo?.storageKey) return undefined;
    const storageKey = photo.storageKey;
    const still = !photo.mimeType || photo.mimeType.startsWith('image/');
    return (still ? this.urls : this.media).resolve(id, async () => {
      const blob = await getBlob(storageKey);
      return blob ? URL.createObjectURL(blob) : undefined;
    });
  }

  /**
   * A url this picture was given turned out not to work. Both the full picture
   * and the small square are forgotten — whichever failed, the other came from
   * the same storage and is just as likely stale — and the old object urls are
   * revoked so the browser can let the memory behind them go. Nothing stored is
   * touched: the next ask reads the picture again and makes a fresh url.
   */
  forgetUrls(id: UUID): void {
    this.urls.forget(id);
    this.thumbs.forget(id);
    this.mediums.forget(id);
    this.media.forget(id);
  }

  /**
   * The small square, made on demand and then kept.
   *
   * A constellation can ask for hundreds of these at once, so four things
   * matter: it is stored (`thumb/<id>`) and survives a reload; the same picture
   * is only ever prepared once however many circles want it at the same moment;
   * only a couple are being *made* at any one time, newest request first (see
   * `smallCopy` — each one decodes a whole photograph, and two hundred at once
   * is what kills a phone's tab); and anything that is not a still image — a
   * clip, a voice note — is handed its own url, because there is nothing to
   * shrink.
   */
  async getThumbUrl(id: UUID, size: 'small' | 'medium' = 'small'): Promise<string | undefined> {
    await this.db.ready;
    const photo = byId(this.db.t.family_photos).get(id);
    if (!photo?.storageKey) return undefined;
    if (!photo.mimeType?.startsWith('image/')) return this.getUrl(id);
    const storageKey = photo.storageKey;
    const url = await (size === 'medium' ? this.mediums : this.thumbs).resolve(id, async () => {
      const small = size === 'medium' ? await smallCopy(mediumKey(id), storageKey, MEDIUM_SIDE, 'whole') : await smallCopy(thumbKey(id), storageKey, THUMB_SIDE);
      return small ? URL.createObjectURL(small) : undefined;
    });
    // A picture that could not be shrunk is shown as it is rather than not at all.
    return url ?? this.getUrl(id);
  }
}
